import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, Star, Eye, MessageCircle, ShieldCheck, Share2, ImageOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PropertyListing, getWhatsAppLink } from "@/data/sample-listings";
import { trackWhatsAppClick, trackTourView } from "@/lib/analytics";
import { incrementWhatsAppClick, incrementTourView, getTourShareUrl } from "@/lib/listings";
import { useToast } from "@/hooks/use-toast";
import { useState } from "react";

interface ListingCardProps {
  listing: PropertyListing;
  index?: number;
}

const categoryLabels: Record<string, string> = {
  'event-centre': 'Event Centre',
  'hotel': 'Hotel',
  'apartment': 'Apartment',
  'office': 'Office',
  'restaurant': 'Restaurant',
};

export const ListingCard = ({ listing, index = 0 }: ListingCardProps) => {
  const { toast } = useToast();
  const [imageError, setImageError] = useState(false);
  const whatsappMessage = `Hello! I'm interested in viewing "${listing.title}" in ${listing.location}. Can I get more information and schedule a visit?`;

  const handleWhatsAppClick = () => {
    trackWhatsAppClick(listing.id, listing.category);
    incrementWhatsAppClick(listing.id).catch(() => {});
  };

  const handleTourClick = () => {
    trackTourView(listing.id, listing.title);
    incrementTourView(listing.id).catch(() => {});
  };

  const handleShare = () => {
    const url = getTourShareUrl(listing.slug);
    navigator.clipboard.writeText(url)
      .then(() => toast({ title: "Tour link copied!", description: "Share it on WhatsApp for a branded preview." }))
      .catch(() => toast({ title: "Link copied!", description: url }));
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      className="group bg-card rounded-2xl overflow-hidden shadow-md card-hover border border-border"
    >
      {/* Image Container */}
      <div className="relative aspect-[4/3] overflow-hidden bg-muted">
        {!imageError && listing.imageUrl ? (
          <img
            src={listing.imageUrl}
            alt={listing.title}
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
            loading="lazy"
            fetchPriority="low"
            onError={() => setImageError(true)}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-muted">
            <div className="text-center">
              <ImageOff className="w-12 h-12 text-muted-foreground mx-auto mb-2" />
              <p className="text-xs text-muted-foreground">Image unavailable</p>
            </div>
          </div>
        )}
        
        {/* Overlay Gradient */}
        <div className="absolute inset-0 bg-gradient-to-t from-foreground/80 via-foreground/20 to-transparent" />

        {/* Category Badge */}
        <div className="absolute top-4 left-4">
          <span className="px-3 py-1.5 rounded-full bg-background/90 backdrop-blur-sm text-sm font-medium text-foreground">
            {categoryLabels[listing.category]}
          </span>
        </div>

        {/* Featured Badge */}
        {listing.featured && (
          <div className="absolute top-4 right-4">
            <span className="badge-360">Featured</span>
          </div>
        )}

        {/* Verified Badge */}
        {listing.verified && !listing.featured && (
          <div className="absolute top-4 right-4">
            <span className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-primary/90 backdrop-blur-sm text-primary-foreground text-xs font-semibold">
              <ShieldCheck className="w-3.5 h-3.5" />
              Verified
            </span>
          </div>
        )}

        {/* Quick Actions on Hover */}
        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <Link to={`/tour/${listing.slug}`} onClick={handleTourClick}>
            <Button size="lg" className="gold-gradient text-foreground font-semibold shadow-gold">
              <Eye className="w-5 h-5 mr-2" />
              View 360° Tour
            </Button>
          </Link>
        </div>

        {/* Bottom Info */}
        <div className="absolute bottom-4 left-4 right-4">
          <div className="flex items-center justify-between text-background">
            <div className="flex items-center gap-1">
              <Star className="w-4 h-4 fill-secondary text-secondary" />
              <span className="font-semibold">{listing.rating}</span>
              <span className="text-background/70">({listing.reviewCount})</span>
            </div>
            <div className="flex items-center gap-1.5 text-sm">
              <MapPin className="w-4 h-4" />
              <span>{listing.city.charAt(0).toUpperCase() + listing.city.slice(1)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-5">
        <h3 className="font-display font-bold text-lg text-foreground mb-1 group-hover:text-primary transition-colors">
          {listing.title}
        </h3>
        <p className="text-muted-foreground text-sm mb-3 flex items-center gap-1">
          <MapPin className="w-3.5 h-3.5" />
          {listing.location}
        </p>

        {/* Amenities Preview */}
        <div className="flex flex-wrap gap-1.5 mb-4">
          {listing.amenities.slice(0, 3).map((amenity) => (
            <span 
              key={amenity}
              className="px-2 py-1 rounded-md bg-muted text-xs font-medium text-muted-foreground"
            >
              {amenity}
            </span>
          ))}
          {listing.amenities.length > 3 && (
            <span className="px-2 py-1 rounded-md bg-muted text-xs font-medium text-primary">
              +{listing.amenities.length - 3} more
            </span>
          )}
        </div>

        {/* Architect Credit */}
        {listing.architectFirm && (
          <div className="mb-4">
            <Link
              to={`/tours?search=${encodeURIComponent(listing.architectFirm)}`}
              className="architect-badge text-xs"
            >
              Architecture by {listing.architectFirm}
            </Link>
          </div>
        )}

        {/* Price & CTA */}
        <div className="pt-4 border-t border-border">
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="font-display font-bold text-xl text-primary">{listing.price}</p>
              <p className="text-xs text-muted-foreground">{listing.priceType.replace('-', ' ')}</p>
            </div>
            <Button
              size="sm"
              variant="ghost"
              onClick={handleShare}
              className="gap-1.5 text-muted-foreground hover:text-foreground"
              data-testid={`button-share-${listing.id}`}
            >
              <Share2 className="w-4 h-4" />
              Share
            </Button>
          </div>
          <div className="flex gap-2">
            <a
              href={getWhatsAppLink(`Hello Vista360, I am interested in "${listing.title}" at ${listing.location}. I'd like to book an inspection.`)}
              target="_blank"
              rel="noopener noreferrer"
              onClick={handleWhatsAppClick}
              className="flex-1"
            >
              <Button size="sm" className="whatsapp-btn text-sm w-full">
                <MessageCircle className="w-4 h-4" />
                Book Inspection
              </Button>
            </a>
          </div>
        </div>
      </div>
    </motion.div>
  );
};
